/**
 * /usecases — the index. A PAGE; AppLayoutRoute supplies the chrome.
 *
 * ── Why this exists ───────────────────────────────────────────────────────
 * The five use-case pages were ported without one, which left them reachable
 * only by typing the URL: nothing in the header, the footer or the sitemap
 * pointed at them. Five pages of real writing, invisible.
 *
 * It reads from the same `useCases` array the pages themselves render, so a
 * sixth case appears here the moment it is added to the data — there is no
 * second list to keep in step.
 */
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronRight, Briefcase } from 'lucide-react';
import { SEO } from '@/shared/SEO';
import { useCases } from '../data/cases';

export default function UseCasesPage() {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Use Cases — When Businesses Call Us In | Bloomneo"
        description="The situations businesses come to us in — a developer who left, an agency gone quiet, five providers and no clear bill."
        keywords="managed infrastructure use cases, developer left, agency handover, consolidate hosting"
      />

      <section className="relative isolate overflow-hidden rounded-3xl border border-border bg-muted/20 px-4 py-12 sm:px-6 md:py-16">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 opacity-[0.06]"
          style={{
            backgroundImage:
              'linear-gradient(var(--color-brand-mark) 1px, transparent 1px),' +
              'linear-gradient(90deg, var(--color-brand-mark) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
            maskImage: 'radial-gradient(ellipse at center top, black 40%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center top, black 40%, transparent 75%)',
          }}
        />

        <div className="bloom-rise mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2.5 rounded-full border border-primary/20 bg-primary/10 px-4 py-1.5 mb-6">
            <Briefcase className="h-3.5 w-3.5 text-primary" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary">
              Use cases
            </span>
          </div>

          <h1
            className="font-bold tracking-[-0.03em] leading-[1.02]"
            style={{ fontSize: 'clamp(1.9rem, 4vw, 2.8rem)' }}
          >
            Nobody calls us because they want to change hosting.
          </h1>

          <p className="mt-5 text-base text-muted-foreground leading-relaxed">
            They call because something has already gone wrong, or is about to. These are the
            five situations we see most, and what we do in the first week of each.
          </p>
        </div>
      </section>

      <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2">
        {useCases.map((useCase, i) => (
          /*
             A link, not a button. An onClick navigation is invisible to anything
             that does not run JavaScript: a crawler cannot follow it, the build's
             pre-renderer cannot find the page behind it, and a reader cannot open
             it in a new tab. Five pages of writing were reachable only by typing
             the URL or reading the sitemap.
          */
          <Link
            key={useCase.slug}
            to={`/usecases/${useCase.slug}`}
            className={`bloom-rise bloom-rise-${Math.min(i + 1, 4)} group flex h-full flex-col rounded-2xl border border-border bg-card p-6 text-left transition-colors hover:border-primary/40 hover:bg-muted/30`}
          >
            <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary">
              {useCase.eyebrow}
            </span>

            <h2 className="mt-3 text-lg font-semibold tracking-tight leading-snug">
              {useCase.title}
            </h2>

            <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">
              {useCase.description}
            </p>

            <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary">
              What we do about it
              <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </span>
          </Link>
        ))}
      </div>

      <section className="bloom-rise mt-14 mb-4 rounded-2xl bg-primary px-7 py-12 text-primary-foreground md:px-12 md:py-14">
        <h2
          className="font-bold tracking-[-0.02em] leading-[1.1] max-w-lg"
          style={{ fontSize: 'clamp(1.45rem, 2.6vw, 1.95rem)' }}
        >
          None of these quite fit?
        </h2>
        <p className="mt-4 max-w-md leading-relaxed opacity-80">
          They rarely do exactly. Tell us what you are running and what is worrying you, and we
          will tell you plainly whether we can help.
        </p>
        <button
          onClick={() => navigate('/contact')}
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-background px-7 py-3 text-sm font-semibold text-foreground transition-transform hover:-translate-y-0.5"
        >
          Talk to us <ArrowRight className="h-4 w-4" />
        </button>
      </section>
    </>
  );
}
